import { Injectable } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class PerformanceLoggerService {
    private marks = new Map<string, number>();
    private measurements = new Map<string, number>();

    constructor() {
        // Log initial performance metrics
        this.logNavigationTiming();
    }

    /**
     * Start timing an operation
     */
    startTiming(name: string): void {
        this.marks.set(name, performance.now());
        if (performance.mark) {
            performance.mark(`${name}-start`);
        }
    }

    /**
     * End timing an operation and log the result
     */
    endTiming(name: string): number {
        const startTime = this.marks.get(name);
        if (!startTime) {
            console.warn(`No start time found for ${name}`);
            return 0;
        }

        const endTime = performance.now();
        const duration = endTime - startTime;
        this.measurements.set(name, duration);

        if (performance.mark && performance.measure) {
            performance.mark(`${name}-end`);
            performance.measure(name, `${name}-start`, `${name}-end`);
        }

        console.log(`⏱️ ${name}: ${duration.toFixed(2)}ms`);
        return duration;
    }

    /**
     * Log navigation timing API data using modern PerformanceNavigationTiming
     */
    private logNavigationTiming(): void {
        if (typeof window !== 'undefined' && window.performance) {
            // Wait for load event to ensure all metrics are available
            if (document.readyState === 'complete') {
                this.logTimingMetrics();
            } else {
                window.addEventListener('load', () => {
                    setTimeout(() => this.logTimingMetrics(), 0);
                });
            }
        }
    }

    private logTimingMetrics(): void {
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (navEntries.length > 0) {
            this.logNavigationTimingMetrics(navEntries[0]);
        }
    }

    private logNavigationTimingMetrics(timing: PerformanceNavigationTiming): void {
        const metrics = {
            'DNS Lookup': timing.domainLookupEnd - timing.domainLookupStart,
            'TCP Connection': timing.connectEnd - timing.connectStart,
            'Request Time': timing.responseStart - timing.requestStart,
            'Response Time': timing.responseEnd - timing.responseStart,
            'DOM Processing': timing.domComplete - timing.domInteractive,
            'Load Event': timing.loadEventEnd - timing.loadEventStart,
            'Total Page Load': timing.loadEventEnd - timing.fetchStart,
            'DOM Content Loaded': timing.domContentLoadedEventEnd - timing.domContentLoadedEventStart,
            'Time to First Byte': timing.responseStart - timing.fetchStart,
            'DOM Interactive': timing.domInteractive - timing.fetchStart,
            'Redirect Time': timing.redirectEnd - timing.redirectStart,
            'Unload Time': timing.unloadEventEnd - timing.unloadEventStart
        };

        console.group('🚀 Page Load Performance Metrics');
        Object.entries(metrics).forEach(([name, value]) => {
            if (value > 0) {
                const color = value > 1000 ? 'color: red' : value > 500 ? 'color: orange' : 'color: green';
                console.log(`%c${name}: ${value.toFixed(2)}ms`, color);
            }
        });
        console.groupEnd();

        // Log warnings for slow metrics
        if (metrics['Total Page Load'] > 3000) {
            console.warn('⚠️ Page load time is over 3 seconds! Consider optimizing.');
        }
        if (metrics['Time to First Byte'] > 1000) {
            console.warn('⚠️ Time to First Byte is over 1 second! Check server performance.');
        }
    }

    private isEntryTypeSupported(entryType: string): boolean {
        return (
            typeof PerformanceObserver !== 'undefined' &&
            Array.isArray(PerformanceObserver.supportedEntryTypes) &&
            PerformanceObserver.supportedEntryTypes.includes(entryType)
        );
    }

    /**
     * Get largest contentful paint
     */
    getLCP(): void {
        if (!this.isEntryTypeSupported('largest-contentful-paint')) {
            return;
        }
        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                const lastEntry = entries[entries.length - 1];
                console.log(`🎨 Largest Contentful Paint: ${lastEntry.startTime.toFixed(2)}ms`);
            });
            observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch {
            // Browser advertised support but observe() still rejected the type.
        }
    }

    /**
     * Get first input delay
     */
    getFID(): void {
        if (!this.isEntryTypeSupported('first-input')) {
            return;
        }
        try {
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach((entry: PerformanceEventTiming) => {
                    console.log(`⚡ First Input Delay: ${entry.processingStart - entry.startTime}ms`);
                });
            });
            observer.observe({ entryTypes: ['first-input'] });
        } catch {
            // Browser advertised support but observe() still rejected the type.
        }
    }

    /**
     * Get cumulative layout shift
     */
    getCLS(): void {
        if (!this.isEntryTypeSupported('layout-shift')) {
            return;
        }
        try {
            let clsValue = 0;
            const observer = new PerformanceObserver((list) => {
                const entries = list.getEntries();
                entries.forEach((entry) => {
                    const layoutShift = entry as PerformanceEntry & {
                        hadRecentInput?: boolean;
                        value?: number;
                    };
                    if (!layoutShift.hadRecentInput) {
                        clsValue += layoutShift.value ?? 0;
                    }
                });
                console.log(`📐 Cumulative Layout Shift: ${clsValue.toFixed(4)}`);
            });
            observer.observe({ entryTypes: ['layout-shift'] });
        } catch {
            // Browser advertised support but observe() still rejected the type.
        }
    }

    /**
     * Initialize all Web Vitals measurements
     */
    initWebVitals(): void {
        this.getLCP();
        this.getFID();
        this.getCLS();
    }

    /**
     * Get all measurements
     */
    getAllMeasurements(): Map<string, number> {
        return new Map(this.measurements);
    }
} 